import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  TextField,
  Typography,
  Alert,
  Stack,
  Divider,
} from "@mui/material";
import {
  Add,
  SearchRounded,
  RestartAltRounded,
  PictureAsPdfRounded,
  TableChartRounded,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { LoadingButton } from "@mui/lab";
import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import { DefaultPage } from "../../../layouts/default-page";
import { DataTableFive } from "../../../layouts/layout2/data-table-five";
import { StatusChip } from "../../common/StatusChip";
import { RowActions } from "../../common/RowActions";
import {
  useAddCommitteeMutation,
  useGetCommitteesQuery,
  useUpdateCommitteeMutation,
} from "../../../services/appointees.query";
import { ExportLoadingBackdrop } from "../../common/ExportLoadingBackdrop";
import { useTableState } from "../../../hooks/useTableState";
import { Notify } from "../../../layouts/notify";

const validationSchema = Yup.object({
  name: Yup.string().required("Committee Name is required"),
  is_active: Yup.boolean().required("Status is required"),
});

export default function Committees() {
  const navigate = useNavigate();

  const defaultFilters = useMemo(
    () => ({
      search: "",
    }),
    []
  );

  const {
    page,
    pageSize,
    filters,
    hasActiveFilters,
    setPage,
    setFilters,
    resetFilters,
  } = useTableState(defaultFilters, {
    defaultPage: 1,
    defaultPageSize: 100,
  });

  const [draftSearch, setDraftSearch] = useState(filters.search);

  useEffect(() => {
    setDraftSearch(filters.search);
  }, [filters]);

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [editMode, setEditMode] = useState<boolean>(false);

  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState("");

  const [addCommittee, { isLoading: addCommitteeLoading }] = useAddCommitteeMutation();
  const [updateCommittee, { isLoading: updateCommitteeLoading }] = useUpdateCommitteeMutation();

  const {
    data: committees = [],
    isLoading,
    isFetching,
    isError,
  } = useGetCommitteesQuery("");

  const viewMembers = (rowData: any) => {
    navigate("/app-contents/committee-members", { state: { committeeData: rowData } });
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      is_active: true,
    },
    validationSchema: validationSchema,
    onSubmit: async (values: any) => {
      try {
        if (editMode) {
          await updateCommittee({
            ...values,
            id: selectedItem.id,
          }).unwrap();
          Notify("info", "Committee updated successfully");
        } else {
          await addCommittee({
            ...values,
          }).unwrap();
          Notify("info", "Committee added successfully");
        }
        setOpenModal(false);
      } catch (error: any) {
        console.error("Error during form submission:", error);
        Notify("error", error?.message || "Failed to save committee");
      }
    },
  });

  const openAddEditModal = (committee: any = null) => {
    if (committee) {
      setEditMode(true);
      setSelectedItem(committee);
      formik.setValues({
        name: committee.name || "",
        is_active: Boolean(committee.is_active),
      });
    } else {
      formik.resetForm();
      setEditMode(false);
    }
    setOpenModal(true);
  };

  const handleSearch = () => {
    setFilters({
      search: draftSearch.trim(),
    });
  };

  const handleClear = () => {
    resetFilters();
  };

  const handleEnterSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const filteredData = useMemo(() => {
    if (!filters.search) return committees;
    const query = filters.search.toLowerCase();
    return committees.filter((item: any) =>
      `${item?.name || ""}`.toLowerCase().includes(query)
    );
  }, [committees, filters.search]);

  const totalCount = filteredData.length;
  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, page, pageSize]);

  const handleExportPDF = async () => {
    if (filteredData.length === 0) {
      Notify("info", "No records found to export.");
      return;
    }

    setIsExporting(true);
    setExportMessage(`Preparing PDF document for all ${filteredData.length.toLocaleString()} committees...`);
    await new Promise((resolve) => setTimeout(resolve, 50));

    try {
      const doc = new jsPDF("portrait", "mm", "a4");
      doc.setFontSize(14);
      doc.setTextColor(0, 114, 188);
      doc.text("MTRCB - Board Committees Directory", 14, 15);

      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(
        `Generated on: ${format(new Date(), "MMMM dd, yyyy · hh:mm a")} | Total Committees: ${filteredData.length}`,
        14,
        22
      );

      const tableColumn = ["#", "Committee Name", "Active Status"];
      const tableRows = filteredData.map((item: any, idx: number) => [
        idx + 1,
        item.name || "—",
        item.is_active ? "ACTIVE" : "INACTIVE",
      ]);

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 27,
        theme: "grid",
        headStyles: {
          fillColor: [0, 114, 188],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 9,
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        styles: {
          fontSize: 8.5,
          cellPadding: 3,
          textColor: [15, 23, 42],
          overflow: "linebreak",
        },
        margin: { top: 12, left: 14, right: 14, bottom: 12 },
        didDrawPage: (data: any) => {
          const pageCount = (doc as any).internal.getNumberOfPages();
          doc.setFontSize(7.5);
          doc.setTextColor(148, 163, 184);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            doc.internal.pageSize.getWidth() - 30,
            doc.internal.pageSize.getHeight() - 6
          );
        },
      });

      doc.save(`MTRCB_Committees_${format(new Date(), "yyyyMMdd_HHmm")}.pdf`);
      Notify("success", `Exported all ${filteredData.length} committees to PDF.`);
    } catch (err: any) {
      console.error("PDF export error:", err);
      Notify("error", err?.message || "Failed to generate PDF export.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    if (filteredData.length === 0) {
      Notify("info", "No records found to export.");
      return;
    }

    setIsExporting(true);
    setExportMessage(`Generating Excel spreadsheet for all ${filteredData.length.toLocaleString()} committees...`);
    await new Promise((resolve) => setTimeout(resolve, 50));

    try {
      const rows = filteredData.map((item: any, idx: number) => ({
        "No.": idx + 1,
        "Committee Name": item.name || "—",
        "Status": item.is_active ? "ACTIVE" : "INACTIVE",
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Committees");

      ws["!cols"] = [
        { wch: 6 },
        { wch: 45 },
        { wch: 16 },
      ];

      XLSX.writeFile(wb, `MTRCB_Committees_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`);
      Notify("success", `Exported all ${filteredData.length} committees to Excel.`);
    } catch (err: any) {
      console.error("Excel export error:", err);
      Notify("error", err?.message || "Failed to generate Excel export.");
    } finally {
      setIsExporting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "rowNumber",
        header: "No.",
        size: 70,
        enableSorting: false,
        Cell: ({ row }: any) => (
          <Typography>{(page - 1) * pageSize + row.index + 1}</Typography>
        ),
      },
      {
        accessorKey: "name",
        header: "Committee Name",
        size: 380,
        Cell: ({ cell }: any) => (
          <Typography fontWeight={600} color="#1E293B">
            {cell.getValue() || "—"}
          </Typography>
        ),
      },
      {
        accessorKey: "is_active",
        header: "Status",
        size: 140,
        Cell: ({ row }: any) => {
          const isActive = row.original.is_active;
          return (
            <StatusChip
              label={isActive ? "ACTIVE" : "INACTIVE"}
              tone={isActive ? "success" : "neutral"}
            />
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        size: 160,
        enableSorting: false,
        Cell: ({ row }: any) => (
          <RowActions
            actions={[
              {
                label: "Members",
                onClick: () => viewMembers(row.original),
              },
              {
                label: "Edit",
                onClick: () => openAddEditModal(row.original),
              },
            ]}
          />
        ),
      },
    ],
    [page, pageSize]
  );

  return (
    <DefaultPage
      title="Committees"
      subTitle="Manage board committees and member assignments."
      primaryButtonProps={{
        startIcon: <Add />,
        children: "Add New Committee",
        onClick: () => openAddEditModal(),
      }}
      loading={isLoading}
    >
      <Box mb={2.5}>
        <Alert
          severity="info"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BAE6FD",
            backgroundColor: "#F0F9FF",
            "& .MuiAlert-icon": { color: "#0072BC" },
          }}
        >
          Displays registered board committees. Set filters and click <strong>Search</strong> to load results.
        </Alert>
      </Box>

      {isError && (
        <Box mb={2.5}>
          <Alert severity="error" sx={{ borderRadius: "6px", fontSize: 13 }}>
            Failed to load committees.
          </Alert>
        </Box>
      )}

      {/* Standardized Gold-Standard Filter Card */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: "6px",
          p: 2,
          mb: 2,
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <TextField
              size="small"
              label="Search Committees"
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              onKeyDown={handleEnterSearch}
              sx={{
                minWidth: 320,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            />
          </Stack>

          <Divider sx={{ borderColor: "#F1F5F9" }} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<SearchRounded fontSize="small" />}
                onClick={handleSearch}
                disabled={isFetching}
                sx={{
                  backgroundColor: "#0072BC",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: 13,
                  px: 2,
                  py: 0.75,
                  borderRadius: "4px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#005a96",
                    boxShadow: "none",
                  },
                }}
              >
                {isFetching ? "Searching..." : "Search"}
              </Button>

              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RestartAltRounded fontSize="small" />}
                  onClick={handleClear}
                  disabled={isFetching}
                  sx={{
                    color: "#64748B",
                    borderColor: "#CBD5E1",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: "4px",
                    "&:hover": {
                      borderColor: "#94A3B8",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                >
                  Clear
                </Button>
              )}
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfRounded fontSize="small" />}
                onClick={handleExportPDF}
                disabled={isExporting}
                sx={{
                  color: "#334155",
                  borderColor: "#CBD5E1",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#94A3B8",
                    backgroundColor: "#F8FAFC",
                  },
                }}
              >
                Export PDF
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<TableChartRounded fontSize="small" />}
                onClick={handleExportExcel}
                disabled={isExporting}
                sx={{
                  color: "#0072BC",
                  borderColor: "#BAE6FD",
                  backgroundColor: "#F0F9FF",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#0072BC",
                    backgroundColor: "#E0F2FE",
                  },
                }}
              >
                Export Excel
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {/* Matched Records Banner */}
      <Box mb={2}>
        <Alert
          severity="success"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BBF7D0",
            backgroundColor: "#F0FDF4",
            py: 0.5,
            "& .MuiAlert-icon": { color: "#00934C" },
          }}
        >
          Total matched records: <strong>{Number(totalCount || 0).toLocaleString()}</strong>
        </Alert>
      </Box>

      <DataTableFive
        data={paginatedData}
        total={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        columns={columns as any}
        getRowId={(row: { id: any }) => String(row.id)}
        isLoading={isLoading}
        isFetching={isFetching}
        onRowClick={(row: any) => viewMembers(row.original)}
      />

      <ExportLoadingBackdrop open={isExporting} message={exportMessage} />

      {/* Modern, Clean Edit/Add Modal with Strict 6px Border Radius */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "6px",
            border: "1px solid #E2E8F0",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 18, borderBottom: "1px solid #F1F5F9" }}>
          {editMode ? "Edit Committee" : "Add New Committee"}
        </DialogTitle>
        <form noValidate onSubmit={formik.handleSubmit}>
          <DialogContent sx={{ p: 3 }}>
            <Stack spacing={2.5}>
              <FormControl fullWidth>
                <TextField
                  size="small"
                  label="Committee Name"
                  name="name"
                  id="name"
                  variant="outlined"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && (formik.errors.name as any)}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "4px" },
                  }}
                />
              </FormControl>

              <FormControlLabel
                control={
                  <Checkbox
                    name="is_active"
                    checked={formik.values.is_active}
                    onChange={formik.handleChange}
                    color="primary"
                  />
                }
                label="Active Status"
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 1, borderTop: "1px solid #F1F5F9" }}>
            <Button
              onClick={handleCloseModal}
              sx={{ borderRadius: "4px", textTransform: "none", color: "#64748B" }}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              type="submit"
              loading={addCommitteeLoading || updateCommitteeLoading || formik.isSubmitting}
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                backgroundColor: "#0072BC",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#005a96" },
              }}
            >
              Save Committee
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </DefaultPage>
  );
}
